/**
 * A blog article.
 *
 * Every published article is prerendered at build time (scripts/prerender.mjs
 * with src/entry-server.tsx): the HTML already holds the article, and the data
 * below comes along as a React Query cache, so the browser's first render is
 * the page already on screen. That makes three rules for this file:
 *
 *   - The first render must not depend on the browser: no window, no clock,
 *     no time zone (dates are printed in UTC), no random values.
 *   - Nothing above the fold starts invisible. An element faded in from
 *     opacity 0 is not counted as painted until the animation runs, which
 *     would undo what prerendering buys.
 *   - Scrolling must not re-render the page: the article body is sanitized
 *     once (useMemo), and the reading bar is moved through a ref.
 *
 * Articles published after the last build still work: the edge function
 * serves the app shell and this page fetches the same queries.
 */
import { HOME_THEME, SANS } from "@/components/home-a/theme";
import { cdnImage, cdnSrcSet } from "@/lib/imageCdn";
import { BrandFonts } from "@/components/home-a/BrandSurface";
import { useEffect, useMemo, useRef, useState } from "react";
import { shareOnLinkedIn, shareOnFacebook, shareOnReddit } from "@/utils/shareSocial";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Clock, Eye, ArrowLeft, Share2, Facebook, Linkedin, TrendingUp, Mail, ChevronRight, BookOpen, ArrowUp, Hash } from "lucide-react";
import { getSimulatedViewCount, formatViewCount } from "@/utils/simulatedViews";
import { injectInternalLinks } from "@/utils/internalLinker";
import eyecaptainIcon from "@/assets/eyecaptain-icon-sm.webp";
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";
import MegaMenu from "@/components/LazyMegaMenu";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import BlogArticleSEO from "@/components/BlogArticleSEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { BlogNewsletterCTA } from "@/components/newsletter/BlogNewsletterCTA";
import { ArticleShareButtons } from "@/components/blog/ArticleShareButtons";
import { ConversionsBoosterBanner } from "@/components/blog/ConversionsBoosterBanner";
import { trackBusinessEvent } from "@/lib/track-event";
import RelatedResources, { getPageLinks } from '@/components/RelatedResources';
import { getAuthor, DEFAULT_AUTHOR_SLUG } from '@/config/authors';
import { subscribeNewsletter } from "@/lib/leads";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import {
  articleImagesQuery,
  articleQuery,
  articleWordCount,
  formatArticleDate,
  ownBanner,
  popularArticlesQuery,
  readingMinutes,
  relatedArticlesQuery,
  siblingBannerQuery,
} from "@/lib/blogArticleQueries";

interface BannerTemplate {
  id: string;
  category: string;
  template_url: string;
  color_hex: string;
}

import articleLogo from "@/assets/eyecaptain-logo-dark.webp";
import { articleInlineMedia } from "@/lib/articleInlineMedia";

const SITE = "https://eyecaptain.io";
const WATERMARK = articleLogo;

/* ------------------------------------------------------- article body */

const H2_CLASS = "text-2xl md:text-[1.75rem] font-semibold mt-14 mb-6 text-foreground tracking-[-0.025em] pl-5 border-l-[3px] border-primary";
const H3_CLASS = "text-xl md:text-2xl font-semibold mt-12 mb-5 text-foreground/90 tracking-[-0.025em]";
const H4_CLASS = "text-lg md:text-xl font-semibold mt-10 mb-4 text-foreground/80 tracking-[-0.025em]";
const P_CLASS = "mb-7 leading-[1.95] text-foreground/70 text-[1.0625rem] md:text-[1.125rem]";
const DROP_CAP = "first-letter:text-[4rem] first-letter:font-semibold first-letter:text-primary first-letter:float-left first-letter:mr-3 first-letter:mt-1.5 first-letter:leading-none";

/**
 * Stored article text (HTML, or markdown-like text from older generations) as
 * styled, sanitized HTML.
 *
 * Heading levels: the page title is the only H1. Articles written with H2
 * sections keep them as H2 (they used to be pushed down to H3, which left
 * every article with no H2 at all and a gap in the outline). Only an article
 * that uses H1 for its sections is shifted down one level.
 */
function formatArticleBody(raw: string, language: "en" | "el", selfPath?: string): string {
  const sectionDivider = '<div class="flex items-center gap-4 my-16"><div class="flex-1 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div><div class="w-2 h-2 rounded-full bg-primary/40"></div><div class="flex-1 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div></div>';

  let html = raw
    .replace(/\\n/g, "\n")
    // Leftover "html" / "```html" markers from generation.
    .replace(/^```html\s*/i, "")
    .replace(/```\s*$/i, "")
    .replace(/^\s*html\s*\n/i, "");

  // <figure> is left out on purpose: articleInlineMedia adds figures before this
  // runs, and they must not flip plain-text articles into HTML mode.
  const isHTML = /<(h[1-6]|p|ul|ol|li|strong|em|a|div|blockquote)\b/i.test(html);

  if (isHTML) {
    const shift = /<h1[\s>]/i.test(html);
    // The look follows the level the author wrote; only the tag moves down when H1 is in use.
    const tag = (n: number) => `h${Math.min(6, n + (shift ? 1 : 0))}`;
    html = html
      .replace(/<h4(?:\s[^>]*)?>(.*?)<\/h4>/gis, (_m, t) => `<${tag(4)} class="${H4_CLASS}">${t}</${tag(4)}>`)
      .replace(/<h3(?:\s[^>]*)?>(.*?)<\/h3>/gis, (_m, t) => `<${tag(3)} class="${H3_CLASS}">${t}</${tag(3)}>`)
      .replace(/<h2(?:\s[^>]*)?>(.*?)<\/h2>/gis, (_m, t) => `<${tag(2)} class="${H2_CLASS}">${t}</${tag(2)}>`)
      .replace(/<h1(?:\s[^>]*)?>(.*?)<\/h1>/gis, (_m, t) => `${sectionDivider}<h2 class="text-[2rem] sm:text-4xl md:text-5xl font-semibold mt-4 mb-10 text-foreground tracking-[-0.025em] leading-[1.15]">${t}</h2>`)
      .replace(/<strong(?:\s[^>]*)?>/gi, '<strong class="font-semibold text-primary">')
      .replace(/<p(?:\s[^>]*)?>/gi, `<p class="${P_CLASS}">`)
      .replace(/<a\s/gi, '<a class="text-primary font-medium border-b border-primary/30 hover:border-primary pb-0.5 transition-all" ')
      .replace(/<ul(?:\s[^>]*)?>/gi, '<ul class="list-none my-8 ml-0 space-y-0.5">')
      .replace(/<li(?:\s[^>]*)?>/gi, '<li class="pl-1 py-2 flex items-start gap-3"><span class="mt-[0.6rem] w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0"></span><span>')
      .replace(/<\/li>/gi, "</span></li>")
      .replace(/<blockquote(?:\s[^>]*)?>/gi, '<blockquote class="border-l-2 border-primary/60 bg-primary/[0.03] pl-6 pr-4 py-5 my-12 text-foreground/60 rounded-r text-lg leading-relaxed">');
    html = html.replace(/<p class="([^"]*)">/, `<p class="$1 ${DROP_CAP}">`);
  } else {
    html = html
      .replace(/\n*ΤΙΤΛΟΣ:\s*\n/g, "\n").replace(/\n*ΥΠΟΤΙΤΛΟΣ:\s*\n/g, "\n")
      .replace(/\n*HEADING:\s*\n/g, "\n").replace(/\n*SUBHEADING:\s*\n/g, "\n")
      .replace(/\n*ΤΙΤΛΟΣ:\s*(.+)/g, "\n## $1").replace(/\n*ΥΠΟΤΙΤΛΟΣ:\s*(.+)/g, "\n### $1")
      .replace(/\n*HEADING:\s*(.+)/g, "\n## $1").replace(/\n*SUBHEADING:\s*(.+)/g, "\n### $1")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/—/g, ". ")
      .replace(/“/g, '"').replace(/”/g, '"').replace(/…/g, "...")
      // # is a section (H2 here), ## an H2, ### an H3: the page title is the only H1.
      .replace(/^####\s+(.+)$/gm, `<h4 class="${H4_CLASS}">$1</h4>`)
      .replace(/^###\s+(.+)$/gm, `<h3 class="${H3_CLASS}">$1</h3>`)
      .replace(/^##\s+(.+)$/gm, `<h2 class="${H2_CLASS}">$1</h2>`)
      .replace(/^#\s+(.+)$/gm, `${sectionDivider}<h2 class="text-[1.75rem] sm:text-4xl md:text-5xl font-semibold mt-4 mb-10 text-foreground tracking-[-0.025em] leading-[1.15]">$1</h2>`)
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-primary">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em class="italic text-foreground/70">$1</em>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary font-medium border-b border-primary/30 hover:border-primary pb-0.5 transition-all" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/^- (.+)/gm, '<li class="pl-1 py-2">$1</li>')
      .replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-none my-8 ml-0 space-y-0.5">$&</ul>')
      .replace(/^\d+\.\s(.+)/gm, '<li class="pl-1 py-2">$1</li>')
      .replace(/\n/g, `</p><p class="${P_CLASS}">`)
      .replace(/^>\s(.+)/gm, '<blockquote class="border-l-2 border-primary/60 bg-primary/[0.03] pl-6 pr-4 py-5 my-12 text-foreground/60 rounded-r text-lg leading-relaxed">$1</blockquote>');

    html = html.replace(/^<p class="([^"]*)">/, `<p class="$1 ${DROP_CAP}">`);
    html = html.replace(/<li class="([^"]*)">/g, '<li class="$1 flex items-start gap-3"><span class="mt-[0.6rem] w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0"></span><span>');
    html = html.replace(/<\/li>/g, "</span></li>");
    if (!html.startsWith("<")) html = `<p class="${P_CLASS} ${DROP_CAP}">${html}</p>`;
  }

  html = injectInternalLinks(html, language, selfPath);

  return sanitizeHtml(html, {
    ADD_TAGS: ["figure", "figcaption", "span"],
    ADD_ATTR: ["loading", "decoding", "class", "target", "rel", "href", "title", "width", "height", "aria-hidden"],
  });
}

/** The body cut in two around the middle (not next to an inline figure), for the banner between. */
function splitBody(formatted: string): [string, string] {
  let splitIdx = formatted.indexOf("</p>", Math.floor(formatted.length / 2));
  if (splitIdx > -1) {
    const nearby = formatted.slice(Math.max(0, splitIdx - 400), splitIdx + 400);
    if (nearby.includes("<figure")) {
      const figureEnd = formatted.indexOf("</figure>", splitIdx);
      if (figureEnd > -1) {
        const nextP = formatted.indexOf("</p>", figureEnd);
        if (nextP > -1) splitIdx = nextP;
      }
    }
  }
  return splitIdx > -1 ? [formatted.slice(0, splitIdx + 4), formatted.slice(splitIdx + 4)] : [formatted, ""];
}

/* --------------------------------------------------------------- page */

export default function BlogArticle() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const lang: "en" | "el" = language === "el" ? "el" : "en";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  // Share visits (?ref=) are counted once per article.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) supabase.functions.invoke("track-share-visit", { body: { ref } }).catch(() => {});
  }, [slug]);

  // Reading bar and "back to top", without re-rendering the article on every scroll event.
  useEffect(() => {
    let frame = 0;
    let shown = false;
    const update = () => {
      frame = 0;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${docHeight > 0 ? Math.min(1, scrollTop / docHeight) : 0})`;
      const show = scrollTop > 600;
      if (show !== shown) { shown = show; setShowScrollTop(show); }
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (frame) cancelAnimationFrame(frame); };
  }, []);

  const { data: article, isLoading, error } = useQuery({ ...articleQuery(slug), enabled: !!slug });

  // A Greek reader is sent to the Greek slug.
  useEffect(() => {
    if (!article) return;
    const targetSlug = lang === "el" ? article.slug_el : article.slug_en;
    if (targetSlug && slug !== targetSlug) navigate(`/blog/${targetSlug}`, { replace: true });
  }, [article, slug, lang, navigate]);

  const { data: popularArticles } = useQuery(popularArticlesQuery);

  const { data: relatedArticles } = useQuery({
    ...relatedArticlesQuery(article ?? { id: "", slug: null, slug_en: null, keywords: null, category: null }),
    enabled: !!article?.id,
  });

  const { data: articleImages } = useQuery({
    ...articleImagesQuery(article?.id ?? ""),
    enabled: !!article?.id,
  });

  const banner = article && articleImages !== undefined ? ownBanner(article, articleImages) : article?.featured_image_url ?? null;

  const { data: siblingBanner } = useQuery({
    ...siblingBannerQuery(article ?? { id: "", topic_id: null, keywords: null, category: null }),
    enabled: !!article?.id && articleImages !== undefined && !banner,
  });

  const effectiveBannerUrl = banner || siblingBanner?.image_url || null;

  // Banner templates are readable by signed-in users only; visitors fall back to the gradient.
  const { data: bannerTemplate } = useQuery({
    queryKey: ["banner-template", article?.category],
    queryFn: async () => {
      const { data, error } = await supabase.from("ai_banner_templates").select("*").eq("category", article!.category!).single();
      if (error) return null;
      return data as BannerTemplate;
    },
    enabled: !!article?.category,
  });

  // "Read" means read: half the page scrolled, or 40 seconds with the tab in
  // view. Until October 2026 it fired on load, so every visit (bots included)
  // counted as a read.
  useEffect(() => {
    if (!article?.id) return;
    let done = false;
    let visibleSeconds = 0;
    const stop = () => {
      window.clearInterval(timer);
      window.removeEventListener("scroll", onScroll);
    };
    const fire = () => {
      if (done) return;
      done = true;
      stop();
      trackBusinessEvent("blog_article_read", {
        slug: article.slug || slug,
        category: article.category,
        reading_time: article.reading_time_minutes,
      });
    };
    const onScroll = () => {
      const el = document.documentElement;
      if (el.scrollHeight > 0 && (window.scrollY + window.innerHeight) / el.scrollHeight >= 0.5) fire();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && ++visibleSeconds >= 40) fire();
    }, 1000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id]);

  const t = (elText: string | null | undefined, enText: string | null | undefined) =>
    lang === "el" ? (elText || enText) : (enText || elText);

  const title = article ? t(article.title_el, article.title_en) || "" : "";
  const content = article ? t(article.content_el, article.content_en) || "" : "";
  const inlineImages = useMemo(
    () => (articleImages ?? []).filter((img) => img.image_type === "infographic" || img.image_type === "inline"),
    [articleImages],
  );
  const [firstHalf, secondHalf] = useMemo(
    () => (content ? splitBody(formatArticleBody(articleInlineMedia(content, inlineImages, title, lang, WATERMARK), lang, `/blog/${article?.slug_en || article?.slug || slug}`)) : ["", ""]),
    [content, inlineImages, title, lang, article?.slug_en, article?.slug, slug],
  );

  const handleShare = async (platform?: "facebook" | "reddit" | "linkedin") => {
    const articleSlug = lang === "el" ? article?.slug_el : article?.slug_en;
    const url = `${SITE}/blog/${articleSlug || slug}`;
    trackBusinessEvent("social_share_clicked", { platform: platform || "copy_link", content_type: "blog_article", slug: article?.slug || slug });
    if (platform === "facebook") shareOnFacebook(url);
    else if (platform === "reddit") shareOnReddit(url, title || "Article");
    else if (platform === "linkedin") shareOnLinkedIn(url);
    else {
      await navigator.clipboard.writeText(url);
      toast.success(lang === "el" ? "Link αντιγράφηκε!" : "Link copied!");
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    try {
      const { data, error } = await subscribeNewsletter({ email, source: `blog_article_${slug}` });
      if (error) throw error;
      if (data.already_subscribed) toast.info(data.message);
      else toast.success(lang === "el" ? "Εγγραφήκατε επιτυχώς!" : "Subscribed successfully!");
      setEmail("");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Subscription error");
    } finally {
      setIsSubscribing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen overflow-x-clip bg-background text-foreground antialiased" style={{ ...HOME_THEME, fontFamily: SANS }}>
        <BrandFonts />
        <MegaMenu />
        <div className="pt-24 md:pt-32 pb-12 md:pb-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-6 w-1/2 mb-8" />
            <Skeleton className="h-[400px] w-full mb-8 rounded-[18px]" />
            <div className="space-y-4">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen overflow-x-clip bg-background text-foreground antialiased" style={{ ...HOME_THEME, fontFamily: SANS }}>
        <BrandFonts />
        <MegaMenu />
        <div className="pt-24 md:pt-32 pb-12 md:pb-20 px-4">
          <div className="container mx-auto max-w-3xl text-center">
            <h1 className="text-2xl font-semibold mb-4">
              {lang === "el" ? "Το άρθρο δεν βρέθηκε" : "Article not found"}
            </h1>
            <Button asChild>
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {lang === "el" ? "Επιστροφή στο Blog" : "Back to Blog"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const metaDescription = t(article.meta_description_el, article.meta_description_en);
  const articleSlugForUrl = (lang === "el" ? article.slug_el : article.slug_en) || article.slug || slug;
  const canonicalUrl = `${SITE}/blog/${article.slug_en || article.slug || slug}`;
  const ogImage = effectiveBannerUrl || `${SITE}/og-default.png`;
  const wordCount = articleWordCount(content);
  const readTime = readingMinutes(wordCount);
  const author = getAuthor(DEFAULT_AUTHOR_SLUG);
  const authorPath = `/authors/${author.slug}`;
  const isGreek = (s: string) => /[Ͱ-Ͽἀ-῿]/.test(s);
  const tags = (() => {
    const kws = article.keywords ?? [];
    if (lang === "el") return kws.some(isGreek) ? kws.filter(isGreek) : kws;
    return kws.filter((kw) => !isGreek(kw));
  })();
  const hrefFor = (r: { slug: string | null; slug_el: string | null; slug_en: string | null }) =>
    `/blog/${(lang === "el" ? r.slug_el || r.slug : r.slug_en || r.slug) || r.slug_en || r.slug_el}`;

  return (
    <div className="min-h-screen overflow-x-clip bg-background text-foreground antialiased" style={{ ...HOME_THEME, fontFamily: SANS }}>
      <BrandFonts />
      {/* Reading progress */}
      <div className="fixed top-0 left-0 w-full h-1 z-[100] bg-border/20" aria-hidden="true">
        <div
          ref={progressRef}
          className="h-full w-full origin-left bg-gradient-to-r from-primary via-primary to-accent"
          style={{ transform: "scaleX(0)" }}
        />
      </div>

      <BlogArticleSEO
        title={title || "EyeCaptain Blog"}
        description={metaDescription || ""}
        slug={articleSlugForUrl}
        slugEl={article.slug_el || undefined}
        slugEn={article.slug_en || undefined}
        publishedAt={article.published_at}
        updatedAt={article.updated_at || undefined}
        authorSlug={DEFAULT_AUTHOR_SLUG}
        image={ogImage}
        keywords={article.keywords || []}
        readingTime={readTime}
        category={article.category || undefined}
        articleBody={content}
        wordCount={wordCount || undefined}
      />

      <MegaMenu />

      {/* Hero */}
      <section className="relative pt-20 md:pt-24 pb-4 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-background" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-1/3 w-[700px] h-[700px] bg-primary/5 rounded-full blur-[200px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/4 rounded-full blur-[180px]" />
        </div>

        <div className="container mx-auto max-w-4xl px-6 relative z-10">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm py-2 mb-2 md:mb-12">
            <Link to="/" className="text-gray-500 hover:text-primary transition-colors font-medium">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
            <Link to="/blog" className="text-gray-500 hover:text-primary transition-colors font-medium">Blog</Link>
            {article.category && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
                <span className="text-primary/70 font-medium">{article.category}</span>
              </>
            )}
          </nav>

          {article.category && (
            <div className="space-y-6">
              <Badge
                className="text-xs font-bold px-4 py-1.5 uppercase tracking-[0.15em] border-0 rounded-full"
                style={{
                  backgroundColor: bannerTemplate?.color_hex ? `${bannerTemplate.color_hex}15` : "hsl(var(--primary) / 0.12)",
                  color: bannerTemplate?.color_hex || "hsl(var(--primary))",
                }}
              >
                {article.category}
              </Badge>
            </div>
          )}
        </div>
      </section>

      {/* Featured image */}
      <section className="px-4 md:-mt-4 mb-6 md:mb-12 relative z-10">
        <figure className="container mx-auto max-w-5xl rounded-[18px] overflow-hidden border border-border shadow-2xl shadow-black/20 relative pb-10" style={{ background: "#0c140f" }}>
          {effectiveBannerUrl || bannerTemplate ? (
            <div className="relative w-full aspect-[1200/630] overflow-hidden">
              <img
                src={cdnImage(effectiveBannerUrl || bannerTemplate!.template_url, 1200)}
                srcSet={cdnSrcSet(effectiveBannerUrl || bannerTemplate!.template_url, [640, 960, 1200, 1600])}
                sizes="(min-width: 1024px) 960px, 100vw"
                alt={effectiveBannerUrl ? title : `${article.category} - ${title}`}
                width={1200}
                height={630}
                className="w-full h-full object-cover object-top"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          ) : (
            <div className="relative w-full aspect-[1200/630] bg-gradient-to-br from-emerald-900/60 via-background to-blue-900/40 overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-emerald-500/20 rounded-full blur-[120px]" />
                <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-blue-500/20 rounded-full blur-[100px]" />
              </div>
              <div className="absolute inset-x-0 bottom-0 z-10 p-5 sm:p-8 lg:p-10">
                <EyeCaptainLogo alt="EyeCaptain" width={144} height={36} className="h-7 sm:h-9 w-auto opacity-50" />
              </div>
            </div>
          )}
          <img src={articleLogo} alt="EyeCaptain" width={104} height={26} className="absolute bottom-2.5 right-4 h-5 w-auto" />
        </figure>
      </section>

      {/* Main content */}
      <section className="px-4 pb-16">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-[1fr_340px] gap-20">
            <article className="min-w-0 max-w-[720px]">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground leading-[1.1] tracking-[-0.025em] mb-6">
                {title}
              </h1>

              {metaDescription && (
                <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-8">{metaDescription}</p>
              )}

              {/* Byline, reading time, tags */}
              <div className="flex flex-wrap items-center gap-4 mb-10 pb-8 border-b border-border/40">
                <div className="flex items-center gap-3">
                  <Link to={authorPath} aria-label={author.name}>
                    <img src={eyecaptainIcon} alt={author.name} width={40} height={40} className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/20" />
                  </Link>
                  <div>
                    <Link to={authorPath} rel="author" className="text-foreground text-sm font-semibold hover:text-primary transition-colors">
                      {author.name}
                    </Link>
                    {article.published_at && (
                      <p className="text-muted-foreground text-xs">
                        <time dateTime={article.published_at}>{formatArticleDate(article.published_at, lang)}</time>
                      </p>
                    )}
                  </div>
                </div>

                <div className="hidden sm:block w-px h-6 bg-border" />

                <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  <span>{readTime} {lang === "el" ? "λεπτά" : "min read"}</span>
                </div>

                <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Eye className="h-4 w-4" aria-hidden="true" />
                  {/* The count moves with the clock, so the browser's value replaces the build's. */}
                  <span suppressHydrationWarning>
                    {`${formatViewCount(getSimulatedViewCount(article.id, article.published_at, article.view_count))} views`}
                  </span>
                </div>

                <div className="hidden sm:block w-px h-6 bg-border" />

                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 6).map((kw) => (
                      <span
                        key={kw}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium text-primary/80 bg-primary/8 border border-primary/10"
                      >
                        <Hash className="h-3 w-3" aria-hidden="true" />
                        {kw}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {firstHalf && <div className="article-content" dangerouslySetInnerHTML={{ __html: firstHalf }} />}

              <ConversionsBoosterBanner category={article.category ?? undefined} keywords={article.keywords ?? undefined} />

              {secondHalf && <div className="article-content" dangerouslySetInnerHTML={{ __html: secondHalf }} />}

              <BlogNewsletterCTA articleSlug={slug} />

              {/* EyeCaptain */}
              <div className="mt-16 p-8 md:p-10 rounded-[18px] bg-gradient-to-br from-primary/10 via-accent/5 to-primary/8 border border-primary/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-[80px]" />
                <div className="relative z-10">
                  <h2 className="text-xl md:text-2xl font-semibold mb-3 text-foreground">
                    {lang === "el" ? "Αύξησε το conversion rate με το EyeCaptain" : "Find what stops your visitors from converting"}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6 text-[1.0625rem]">
                    {lang === "el"
                      ? "Το EyeCaptain είναι ένα AI-powered εργαλείο CRO & UX ανάλυσης που σαρώνει αυτόματα τις σελίδες σου, εντοπίζει UX προβλήματα, και σου δίνει actionable προτάσεις βελτιστοποίησης για να αυξήσεις τις μετατροπές. Δοκίμασέ το δωρεάν, χωρίς κάρτα, χωρίς δέσμευση."
                      : "EyeCaptain is an AI-powered CRO & UX audit tool that automatically scans your pages, identifies UX issues, and gives you actionable optimization suggestions to increase conversions. Try it for free, no card, no commitment."}
                  </p>
                  <Button asChild size="lg" className="ec-cta h-12 rounded-[10px] font-semibold">
                    <Link to="/free-audit">
                      <TrendingUp className="mr-2 h-5 w-5" />
                      {lang === "el" ? "Δωρεάν CRO Audit" : "Free CRO Audit"}
                    </Link>
                  </Button>
                </div>
              </div>

              <ArticleShareButtons title={title} url={canonicalUrl} variant="inline" />

              {/* About the author */}
              <div className="mt-14 p-6 rounded-[18px] border border-border/40 bg-card/30 flex flex-col sm:flex-row gap-5">
                <Link to={authorPath} className="shrink-0" aria-label={author.name}>
                  <img src={eyecaptainIcon} alt="" width={64} height={64} className="w-16 h-16 rounded-xl object-cover ring-2 ring-primary/20" loading="lazy" />
                </Link>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-mint))] mb-1.5">
                    {lang === "el" ? "Σχετικά με τον συγγραφέα" : "About the author"}
                  </p>
                  <Link to={authorPath} rel="author" className="font-bold text-lg hover:text-primary transition-colors">
                    {author.name}
                  </Link>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                    {lang === "el" ? author.shortBioEl : author.shortBioEn}
                  </p>
                  <Link to={authorPath} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-3 hover:underline">
                    {lang === "el" ? `Περισσότερα άρθρα από τον ${author.name}` : `More articles by ${author.name}`}
                  </Link>
                </div>
              </div>

              {/* Popular articles: plain in-content links, crawlable */}
              {popularArticles && popularArticles.length > 0 && (
                <nav className="mt-10 p-6 rounded-[18px] border border-border/40 bg-card/20" aria-label={lang === "el" ? "Δημοφιλή άρθρα" : "Popular articles"}>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" aria-hidden="true" />
                    {lang === "el" ? "Δημοφιλή άρθρα" : "Popular articles"}
                  </h2>
                  <ul className="space-y-2.5">
                    {popularArticles.slice(0, 6).map((pop) => (
                      <li key={pop.id}>
                        <Link to={`/blog/${pop.slug_en || pop.slug || pop.slug_el}`} className="text-[0.95rem] text-muted-foreground hover:text-primary transition-colors">
                          {t(pop.title_el, pop.title_en)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-16 space-y-8">
                <ArticleShareButtons title={title} url={canonicalUrl} variant="sidebar" />

                <div className="p-6 rounded-[18px] bg-gradient-to-br from-primary/8 to-accent/5 border border-primary/15">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-primary/15">
                      <Mail className="w-5 h-5 text-primary" aria-hidden="true" />
                    </div>
                    <h2 className="font-semibold text-lg">Newsletter</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {lang === "el" ? "Μη χάνετε νέα άρθρα & CRO tips. Εγγραφείτε τώρα." : "Don't miss new articles & CRO tips. Subscribe now."}
                  </p>
                  <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                    <Input
                      type="email"
                      aria-label={lang === "el" ? "Το email σας" : "Your email"}
                      placeholder={lang === "el" ? "Το email σας" : "Your email"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-background/60 border-border rounded-xl"
                      required
                    />
                    <Button type="submit" className="w-full rounded-xl" disabled={isSubscribing}>
                      {isSubscribing ? (lang === "el" ? "Εγγραφή..." : "Subscribing...") : lang === "el" ? "Εγγραφή" : "Subscribe"}
                    </Button>
                  </form>
                </div>

                <div className="p-6 rounded-[18px] border border-border/40 bg-card/30">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-xl bg-primary/10">
                      <TrendingUp className="w-5 h-5 text-primary" aria-hidden="true" />
                    </div>
                    <h2 className="font-semibold text-lg">{lang === "el" ? "Δημοφιλή" : "Popular"}</h2>
                  </div>
                  <div className="space-y-3">
                    {popularArticles?.slice(0, 5).map((pop, index) => (
                      <Link key={pop.id} to={hrefFor(pop)} className="group flex gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-all duration-300">
                        {pop.featured_image_url ? (
                          <img
                            src={cdnImage(pop.featured_image_url, 160)}
                            alt=""
                            width={64}
                            height={48}
                            className="shrink-0 w-16 h-12 rounded-lg object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <span className="shrink-0 w-16 h-12 rounded-lg bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="block font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors duration-300 leading-snug">
                            {t(pop.title_el, pop.title_en)}
                          </span>
                          <span className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            {pop.reading_time_minutes || 5} min
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <Button asChild variant="outline" className="w-full rounded-xl">
                  <Link to="/blog">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {lang === "el" ? "Όλα τα Άρθρα" : "All Articles"}
                  </Link>
                </Button>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related articles */}
      {relatedArticles && relatedArticles.length > 0 && (
        <section className="py-14 md:py-24 px-4 border-t border-border/20">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-14">
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-mint))] mb-3">
                {lang === "el" ? "Συνεχίστε την ανάγνωση" : "Continue Reading"}
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-[-0.025em]">
                {lang === "el" ? "Σχετικά Άρθρα" : "Related Articles"}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.slice(0, 3).map((rel) => (
                <Link
                  key={rel.id}
                  to={hrefFor(rel)}
                  className="group block bg-card rounded-[18px] border border-border/40 overflow-hidden hover:border-primary/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  {rel.featured_image_url ? (
                    <div className="overflow-hidden aspect-[16/9]">
                      <img
                        src={cdnImage(rel.featured_image_url, 640)}
                        srcSet={cdnSrcSet(rel.featured_image_url, [400, 640, 960])}
                        sizes="(min-width: 768px) 33vw, 100vw"
                        alt=""
                        width={640}
                        height={360}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[16/9] bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <span className="text-4xl font-bold text-primary/15">EC</span>
                    </div>
                  )}
                  <div className="p-6">
                    {rel.category && <Badge variant="secondary" className="mb-3 text-xs rounded-full">{rel.category}</Badge>}
                    <h3 className="font-semibold text-lg mb-3 line-clamp-2 group-hover:text-primary transition-colors leading-snug">
                      {t(rel.title_el, rel.title_en)}
                    </h3>
                    {rel.reading_time_minutes ? (
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        {rel.reading_time_minutes} {lang === "el" ? "λεπτά" : "min"}
                      </span>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
            {relatedArticles.length > 3 && (
              <ul className="mt-10 grid md:grid-cols-2 gap-x-8 gap-y-2 max-w-4xl mx-auto">
                {relatedArticles.slice(3).map((rel) => (
                  <li key={rel.id}>
                    <Link to={hrefFor(rel)} className="text-[0.95rem] text-muted-foreground hover:text-primary transition-colors">
                      {t(rel.title_el, rel.title_en)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      <RelatedResources
        links={getPageLinks("blog-article")}
        titleEn="Explore Our Tools & Guides"
        titleEl="Εξερεύνησε τα Εργαλεία & Οδηγούς μας"
      />

      {showScrollTop && (
        <button
          type="button"
          aria-label={lang === "el" ? "Στην αρχή" : "Back to top"}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-20 lg:bottom-8 right-6 z-50 w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-primary/90 transition-colors animate-in fade-in zoom-in-90 duration-200"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border p-3.5 z-50">
        <div className="flex items-center justify-between gap-3">
          <Button asChild variant="outline" size="sm" className="flex-1 rounded-full">
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Blog
            </Link>
          </Button>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" aria-label="Share on Facebook" onClick={() => handleShare("facebook")} className="h-11 w-11 rounded-full">
              <Facebook className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Share on Reddit" onClick={() => handleShare("reddit")} className="h-11 w-11 rounded-full">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
            </Button>
            <Button variant="ghost" size="icon" aria-label="Share on LinkedIn" onClick={() => handleShare("linkedin")} className="h-11 w-11 rounded-full">
              <Linkedin className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label={lang === "el" ? "Αντιγραφή link" : "Copy link"} onClick={() => handleShare()} className="h-11 w-11 rounded-full">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
